/**
 * For loading libs, we store them in public to exclude them from bundling (although dynamic imports would also "work") here
 * This is a bit more clear and avoids some overhead
 * We also do in memory caching as well as http caching to avoid re-fetching the same types over and over again
 */
import type { Monaco } from '@monaco-editor/react';
import dedent from 'dedent';
import type { ExecutorVar } from '~/common/vm/protocol';

type PlaywrightTypeSelector = { page?: boolean; expect?: boolean; pageLocators?: boolean };

export function setupTypescript(monaco: Monaco, options?: { disableBuiltinDiagnostics?: boolean }) {
  const tsOpts = monaco.languages.typescript.typescriptDefaults.getCompilerOptions();
  monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
    ...tsOpts,
    target: monaco.languages.typescript.ScriptTarget.ESNext,
    module: monaco.languages.typescript.ModuleKind.ESNext,
    allowJs: true,
    allowNonTsExtensions: true,
    lib: ['esnext', 'dom'],
    typeRoots: Array.from(
      new Set([...(tsOpts.typeRoots || []), 'node_modules/@types', 'node_modules/@playwright/test']),
    ),
  });
  monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
    diagnosticCodesToIgnore: [80001, 7044],
  });

  // Configure Monaco's built-in diagnostics based on whether backend diagnostics are used.
  // This must always be set (not just when disabling) because typescriptDefaults is global -
  // a previous editor that disabled semantic validation would leave it disabled for all editors.
  monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: options?.disableBuiltinDiagnostics ?? false,
    noSyntaxValidation: false, // Keep syntax validation for instant feedback
    diagnosticCodesToIgnore: [80001, 7044],
  });

  // Remove all extra libs for a fresh start (otherwise the vars will be added to the existing ones across editors)
  monaco.languages.typescript.typescriptDefaults.setExtraLibs([]);
}

// Lazy loading cache for type definitions
// Note: be careful if this ever grows too large, as it will be stored in memory for the tab
const typeCache = new Map<string, string>();

async function loadTypeDefinition(filename: string) {
  const cachedTypeDefs = typeCache.get(filename);
  if (cachedTypeDefs) {
    return cachedTypeDefs;
  }

  try {
    const response = await fetch(`/types/${filename}`, {
      // Cache for 1 week - types rarely change
      headers: {
        'Cache-Control': 'public, max-age=604800',
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filename}: ${response.status}`);
    }
    const types = await response.text();
    typeCache.set(filename, types);
    return types;
  } catch (error) {
    console.warn(`Failed to load type definitions from ${filename}:`, error);
  }
  return '';
}

async function addPlaywrightTypes(monaco: Monaco, types?: PlaywrightTypeSelector): Promise<void> {
  // Load Playwright types asynchronously
  const playwrightTypes = await loadTypeDefinition('playwrightTypes.txt');
  if (playwrightTypes) {
    monaco.languages.typescript.typescriptDefaults.addExtraLib(playwrightTypes);
  }

  if (types?.page) {
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      dedent`
      declare var page: import('@playwright/test').Page;
      `,
    );
  }
  if (types?.expect) {
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      dedent`
      declare var expect: import('@playwright/test').Expect;
      `,
    );
  }
  if (types?.pageLocators) {
    // We have to declare `__stablyPage` as a variable (as opposed to `import('@playwright/test').Page` on each line
    // for this to work )
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      dedent`
      declare var __stablyPage: import('@playwright/test').Page;
      declare var getAttribute: typeof __stablyPage.getAttribute;
      declare var getByAltText: typeof __stablyPage.getByAltText;
      declare var getByLabel: typeof __stablyPage.getByLabel;
      declare var getByPlaceholder: typeof __stablyPage.getByPlaceholder;
      declare var getByRole: typeof __stablyPage.getByRole;
      declare var getByTestId: typeof __stablyPage.getByTestId;
      declare var getByText: typeof __stablyPage.getByText;
      declare var getByTitle: typeof __stablyPage.getByTitle;
      declare var locator: typeof __stablyPage.locator;
      `,
    );
  }
}

export function addVarsType(monaco: Monaco, variables: ExecutorVar[]) {
  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    dedent`
    declare var VARS: {${variables.map(({ name, playwrightType: type }) => `${name}: ${type}`).join(';\n')}};
    `,
  );
}

async function addUnPdfTypes(monaco: Monaco) {
  // Load unpdf types asynchronously
  const unpdfTypes = await loadTypeDefinition('unpdfTypes.txt');
  if (unpdfTypes) {
    monaco.languages.typescript.typescriptDefaults.addExtraLib(unpdfTypes);
  }

  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    dedent`
    interface ExtractedImageObject {
      data: Uint8ClampedArray;
      width: number;
      height: number;
      channels: 1 | 3 | 4;
      key: string;
    }

    declare var unpdf: {
      getDocumentProxy: (data: DocumentInitParameters['data'], options?: DocumentInitParameters): Promise<PDFDocumentProxy>;
      getMeta: (data: PDFDocumentProxy) => Promise<{
        info: Record<string, any>;
        metadata: Record<string, any>;
      }>;
      extractText: {
        (data: PDFDocumentProxy, options?: { mergePages?: false }): Promise<{
          totalPages: number;
          text: string[];
        }>;
        (data: PDFDocumentProxy, options: { mergePages: true }): Promise<{
          totalPages: number;
          text: string;
        }>;
      };
      extractLinks: (data: PDFDocumentProxy) => Promise<{
        totalPages: number;
        links: string[];
      }>;
    };
    `,
  );
}

async function addFakerTypes(monaco: Monaco) {
  // Load faker types asynchronously
  const fakerTypes = await loadTypeDefinition('fakerTypes.txt');
  if (fakerTypes) {
    monaco.languages.typescript.typescriptDefaults.addExtraLib(fakerTypes);
  }

  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    dedent`
    declare var faker: import('@faker-js/faker').Faker;
    `,
  );
}

// There is a synchronous callback function in the officeparser types, but the parseOfficeAsync function is good enough for now
function addOfficeParserTypes(monaco: Monaco) {
  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    dedent`
    declare var officeparser: {
      parseOfficeAsync: (file: string | Buffer) => Promise<string>;
    };
    `,
  );
}

export async function addAllLibTypes(monaco: Monaco, options?: { playwrightTypeSelector?: PlaywrightTypeSelector }) {
  await Promise.allSettled([
    addPlaywrightTypes(monaco, options?.playwrightTypeSelector),
    addFakerTypes(monaco),
    addUnPdfTypes(monaco),
  ]);
  addOfficeParserTypes(monaco);
}
